import { isLoggedIn, getCurrentUser } from './AuthService';
import { post, get, put, del } from './apiClient';
import { urls, imageUrl, PLACEHOLDER_IMAGE } from './urlService';

const CART_KEY = 'shopping_cart';

function normalizeApiItem(item) {
  const p = item.Product || {};
  let variant = {};
  if (item.variantId) {
    variant = p.variants?.find(v => v.id === item.variantId) || {};
  }
  if (!variant.price && p.variants?.length) {
    variant = p.variants.find(v => v.label === item.weight) || p.variants[0];
  }
  const price = Number(variant.price) || 0;
  return {
    name: p.name || item.productName || '',
    price,
    mrp: Number(variant.mrp) || 0,
    image: imageUrl(p.image || item.image) || item.image || PLACEHOLDER_IMAGE,
    weight: item.weight || variant.label || '',
    message: item.message || '',
    quantity: item.quantity || 1,
    _cartItemId: item.id,
    _productId: item.productId,
    _variantId: item.variantId,
    _productSlug: p.slug || item.slug || '',
  };
}

function getLocalCart() {
  const data = localStorage.getItem(CART_KEY);
  return data ? JSON.parse(data) : [];
}

function getLocalTotal() {
  return getLocalCart().reduce((t, i) => t + i.quantity, 0);
}

function saveLocalCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { count: getLocalTotal() } }));
}

export const getCartItems = async () => {
  if (isLoggedIn()) {
    try {
      const data = await get(urls.cart);
      return data.map(normalizeApiItem);
    } catch {
      return [];
    }
  }
  return getLocalCart();
};

export const addToCart = async (product, weight, message) => {
  const w = product.weight || weight || '500 gm';
  const m = product.message || message || '';

  if (isLoggedIn()) {
    try {
      const body = {
        productId: product.id || product._productId,
        variantId: product.variantId || product._variantId || null,
        quantity: 1,
        message: m,
        weight: w,
      };
      await post(urls.cart, body);
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { count: await getTotalItemsCount() } }));
      return await getCartItems();
    } catch (err) {
      console.error('API add to cart failed:', err);
      return [];
    }
  }

  let items = getLocalCart();
  const existingIndex = items.findIndex(item => item.name === product.name && item.weight === w);
  if (existingIndex !== -1) {
    items[existingIndex].quantity += 1;
  } else {
    items.push({
      ...product,
      weight: w,
      message: m,
      quantity: 1,
      addedAt: new Date().toISOString(),
    });
  }
  saveLocalCart(items);
  return items;
};

export const getTotalItemsCount = async () => {
  if (isLoggedIn()) {
    try {
      const items = await get(urls.cart);
      const count = items.reduce((total, item) => total + item.quantity, 0);
      return count;
    } catch {
      return 0;
    }
  }
  const items = getLocalCart();
  return items.reduce((total, item) => total + item.quantity, 0);
};

export const removeFromCart = async (indexOrId) => {
  if (isLoggedIn()) {
    try {
      const items = await getCartItems();
      const target = items[indexOrId];
      if (target?._cartItemId) {
        await del(urls.cartItem(target._cartItemId));
      }
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { count: await getTotalItemsCount() } }));
      return await getCartItems();
    } catch {
      return [];
    }
  }
  let items = getLocalCart();
  items.splice(indexOrId, 1);
  saveLocalCart(items);
  return items;
};

export const updateQuantity = async (index, delta) => {
  if (isLoggedIn()) {
    try {
      const items = await getCartItems();
      const target = items[index];
      if (!target) return [];
      const newQty = target.quantity + delta;
      if (newQty <= 0) {
        return await removeFromCart(index);
      }
      if (target._cartItemId) {
        await put(urls.cartItem(target._cartItemId), { quantity: newQty });
      }
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { count: await getTotalItemsCount() } }));
      return await getCartItems();
    } catch {
      return [];
    }
  }
  let items = getLocalCart();
  items[index].quantity += delta;
  if (items[index].quantity <= 0) {
    items.splice(index, 1);
  }
  saveLocalCart(items);
  return items;
};

export const clearCart = async () => {
  if (isLoggedIn()) {
    try {
      const items = await getCartItems();
      for (const item of items) {
        if (item._cartItemId) {
          await del(urls.cartItem(item._cartItemId)).catch(() => {});
        }
      }
    } catch {}
  }
  localStorage.removeItem(CART_KEY);
  window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { count: 0 } }));
};